import React, { useCallback, useState } from 'react';
import { useAuth } from '../AuthContext';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { APIError, useAxios } from '../core/_axios';
import { confirmCodeForEmailAddress, requestCodeForEmailAddress } from '../core/_requests';

const LoginConfirm = () => {
    const { login } = useAuth();

    const axios = useAxios();

    const navigate = useNavigate();

    const [searchParams] = useSearchParams();

    const emailAddress = decodeURIComponent(searchParams.get('e') || '');

    const [sendingCode, setSendingCode] = useState(false);
    const [errorSendingCode, setErrorSendingCode] = useState<string | null>(null);

    const [resendingCode, setResendingCode] = useState(false);
    const [errorResendingCode, setErrorResendingCode] = useState<string | null>(null);

    const [successfullyResentCodeMessage, setSuccessfullyResentCodeMessage] = useState<string | null>(null);

    const [code, setCode] = useState('');

    const handleCodeConfirm = useCallback(async () => {
        if (sendingCode) {
            return;
        }
        setSendingCode(true);
        setErrorSendingCode(null);
        setErrorResendingCode(null);
        setSuccessfullyResentCodeMessage(null);
        try {
            const response = await confirmCodeForEmailAddress(axios, emailAddress, code);
            login(response.api_token);
            
            // Check if user needs onboarding/welcome page
            if (response.user?.public_data?.needs_onboarding) {
                navigate('/welcome');
            } else {
                navigate('/');
            }
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setErrorSendingCode(errorMessage)
            setSendingCode(false)
        }
    }, [axios, code, emailAddress, login, navigate, sendingCode]);

    const onResendCode = useCallback(async () => {
        if (resendingCode) {
            return;
        }
        setResendingCode(true);
        setErrorSendingCode(null);
        setErrorResendingCode(null);
        setSuccessfullyResentCodeMessage(null);
        try {
            await requestCodeForEmailAddress(axios, emailAddress);
            setSuccessfullyResentCodeMessage('Code successfully resent.');
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setErrorResendingCode(errorMessage)
        } finally {
            setResendingCode(false);
        }
    }, [axios, emailAddress, resendingCode]);

    if (!emailAddress) {
        return <Navigate to="/auth/login?error=missing-email" />;
    }

    return (
        <div className="flex items-center justify-center grow bg-center bg-no-repeat page-bg">
            <div className="card max-w-[420px] w-full">
                <form className="card-body flex flex-col gap-5 p-10" id="sign_in_form" onSubmit={(e) => { e.preventDefault(); handleCodeConfirm(); }}>
                    <div className="text-center mb-2.5">
                        <h3 className="text-lg font-semibold text-gray-900 leading-none mb-2.5">
                            Confirm Signature Guestbook Code
                        </h3>
                        {false && <div className="flex items-center justify-center font-medium">
                            <span className="text-2sm text-gray-600 me-1.5">
                                Need an account?
                            </span>
                            <a className="text-2sm link" href="html/demo1/authentication/classic/sign-up.html">
                                Register account
                            </a>
                        </div>}
                    </div>
                    {false && <>
                        <div className="grid grid-cols-2 gap-2.5">
                            <a className="btn btn-light btn-sm justify-center" href="#">
                                <img alt="" className="size-3.5 shrink-0" src="/media/brand-logos/google.svg" />
                                Use Google
                            </a>
                            <a className="btn btn-light btn-sm justify-center" href="#">
                                <img alt="" className="size-3.5 shrink-0 dark:hidden" src="/media/brand-logos/apple-black.svg" />
                                <img alt="" className="size-3.5 shrink-0 light:hidden" src="/media/brand-logos/apple-white.svg" />
                                Use Apple
                            </a>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="border-t border-gray-200 w-full">
                            </span>
                            <span className="text-2xs text-gray-500 font-medium uppercase">
                                Or
                            </span>
                            <span className="border-t border-gray-200 w-full">
                            </span>
                        </div>
                    </>}
                    <div className="text-center text-gray-600 text-2sm">
                        {`We've sent a code to ${emailAddress}. Please enter it below.`}
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="form-label text-gray-900">
                            Enter access code
                        </label>
                        <input
                            id="loginCode"
                            name="loginCode"
                            autoComplete='off'
                            className="input"
                            placeholder="Enter your code" type="text"
                            required
                            value={code} onChange={(e) => setCode(e.target.value)}
                            autoFocus
                        />
                    </div>
                    {false && <><div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between gap-1">
                            <label className="form-label text-gray-900">
                                Password
                            </label>
                            <a className="text-2sm link shrink-0" href="html/demo1/authentication/classic/reset-password/enter-email.html">
                                Forgot Password?
                            </a>
                        </div>
                        <label className="input" data-toggle-password="true">
                            <input name="user_password" placeholder="Enter Password" type="password" value="" />
                            <button className="btn btn-icon" data-toggle-password-trigger="true">
                                <i className="ki-filled ki-eye text-gray-500 toggle-password-active:hidden">
                                </i>
                                <i className="ki-filled ki-eye-slash text-gray-500 hidden toggle-password-active:block">
                                </i>
                            </button>
                        </label>
                    </div>
                        <label className="checkbox-group">
                            <input className="checkbox checkbox-sm" name="check" type="checkbox" value="1" />
                            <span className="checkbox-label">
                                Remember me
                            </span>
                        </label>
                    </>}
                    <button
                        className="btn btn-primary flex justify-center grow"
                        type="submit"
                        disabled={sendingCode}
                    >
                        {!sendingCode ? `Confirm Login Code` : `Confirming...`}
                    </button>
                    {!sendingCode && <button
                        className="btn btn-secondary flex justify-center grow"
                        type="button"
                        disabled={resendingCode}
                        onClick={onResendCode}
                    >
                        {!resendingCode ? `Resend Code` : `Resending...`}
                    </button>}
                    <Link
                        to="/auth/login"
                        className="flex justify-center grow text-gray-600 text-2sm"
                    >
                        Go Back
                    </Link>
                    {(errorSendingCode || errorResendingCode) ? <div>
                        <div className="text-center p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400" role="alert">
                            {errorSendingCode || errorResendingCode}
                        </div>
                    </div> : <>
                        {successfullyResentCodeMessage && <div>
                            <div className="text-center p-4 mb-4 text-sm text-green-800 rounded-lg bg-green-50 dark:bg-gray-800 dark:text-green-400" role="alert">
                                {successfullyResentCodeMessage}
                            </div>
                        </div>}
                    </>}
                </form>
            </div>
        </div>
    );
};

export default LoginConfirm;
